import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { CourseRepository } from '@/infrastructure/repositories/course.repository'
import { createApiClient } from '@/infrastructure/api/client'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

/**
 * Store для списку категорій лендингу по локалі.
 * Завантажує з бекенду тільки ті категорії, для яких є хоча б один курс у вказаній локалі.
 * Дані кешуються по локалі — повторний запит для тієї ж локалі не виконується.
 */
export const useLandingCategoriesStore = defineStore('landingCategories', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('landingCategories')
  // ========== State ==========
  /** locale -> масив id категорій, по яких є курси */
  const categoriesByLocale = ref<Record<string, string[]>>({})
  const loading = ref(false)

  // ========== Repository ==========
  const runtimeConfig = useRuntimeConfig()
  const apiClient = createApiClient({
    baseUrl: runtimeConfig.public.NUXT_APP_LANDING_API,
  })
  const courseRepository = new CourseRepository(apiClient)

  // ========== Getters ==========
  const isLoading = computed(() => loading.value)

  /**
   * Повертає масив id категорій для локалі (з кешу). Якщо ще не завантажено — порожній масив.
   */
  function getCategoryIdsForLocale(locale: string): string[] {
    return categoriesByLocale.value[locale] ?? []
  }

  /**
   * Чи завантажені вже категорії для цієї локалі.
   */
  function isLoadedForLocale(locale: string): boolean {
    return locale in categoriesByLocale.value
  }

  // ========== Actions ==========

  /**
   * Завантажити id категорій для локалі (тільки ті, по яких є курси).
   * Якщо дані вже є в кеші — повторний запит не робиться.
   * @param locale — локаль (наприклад 'uk', 'en')
   * @returns масив id категорій
   */
  async function fetchCategoriesForLocale(locale: string): Promise<string[]> {
    if (!locale) {
      return []
    }
    if (categoriesByLocale.value[locale]) {
      return categoriesByLocale.value[locale]
    }

    loading.value = true
    clearErrors()

    try {
      const ids = await courseRepository.findCategoriesByLocale({ locale })
      categoriesByLocale.value = { ...categoriesByLocale.value, [locale]: ids }
      return ids
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  /**
   * Очистити помилку (наприклад при закритті toast).
   */
  function clearError() {
    clearErrors()
  }

  /**
   * Скинути кеш (наприклад при логауті або зміні API).
   */
  function reset() {
    categoriesByLocale.value = {}
    loading.value = false
    clearErrors()
  }

  return {
    categoriesByLocale,
    loading: isLoading,
    error,
    hasError,
    getCategoryIdsForLocale,
    isLoadedForLocale,
    fetchCategoriesForLocale,
    clearError,
    reset,
  }
})
